import {
  PublicTransportConfiguration,
  HighwaysConfiguration,
  ParkingConfiguration,
  UnsavedChangesDialog,
} from '@shared/components';
import { AlertDescription, Typography, Separator, Loading, Button, Alert } from '@ui/components';
import { useCompanyPolicyData } from '@pages/onboarding/hooks/use-company-policy-data';
import { ZoneType, CityDto } from '@interfaces/types';
import { ServiceType } from '@interfaces/models';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { Info } from '@ui/components/icons';

const nameCompare = <T extends Record<'name', string>>(a: T, b: T) =>
  a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

export const OnboardingCompanyPolicy = () => {
  const { t } = useTranslation();

  const {
    setIsPublicTransportConfigurationEditing,
    setIsHighwaysConfigurationEditing,
    setIsParkingConfigurationEditing,
    setPublicTransportConfiguration,
    saveCompanyPolicyConfiguration,
    setIsUnsavedChangesDialogOpen,
    publicTransportConfiguration,
    isUnsavedChangesDialogOpen,
    setHighwaysConfiguration,
    setParkingConfiguration,
    isGeneralConfigLoading,
    highwaysConfiguration,
    parkingConfiguration,
    isMutationPending,
    companyPolicyData,
    availableServices,
    isQueryLoading,
  } = useCompanyPolicyData();

  const cities = useMemo(
    () => companyPolicyData?.cities?.sort(nameCompare) ?? [],
    [companyPolicyData],
  );

  const zones = useMemo(
    () => companyPolicyData?.zones?.sort(nameCompare) ?? [],
    [companyPolicyData],
  );

  const getCitiesByZoneType = useCallback(
    (zoneType: ZoneType): CityDto[] => {
      return (
        cities
          .filter((city) => {
            const cityCode = city.code;
            return zones.some((zone) => zone.type === zoneType && zone.cityCode === cityCode);
          })
          .sort(nameCompare) ?? []
      );
    },
    [cities, zones],
  );

  const getZonesOfType = useCallback(
    (type: ZoneType) => zones.filter((zone) => zone.type === type),
    [zones],
  );

  if (isQueryLoading || isGeneralConfigLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-8">
      <Typography variant="h400" element="h1">
        {t('ONBOARDING.COMPANY_POLICY.TERMS_OF_SERVICES')}
      </Typography>
      <Typography variant="paragraphLargeBook">
        {t('ONBOARDING.COMPANY_POLICY.TERMS_OF_SERVICES_DESCRIPTION')}
      </Typography>

      <Typography variant="h200">{t('ONBOARDING.COMPANY_POLICY.DEFAULT_TERMS')}</Typography>

      <div className="space-y-6">
        {availableServices.includes(ServiceType.PARKINGS) && (
          <ParkingConfiguration
            onConfigurationChange={setParkingConfiguration}
            setIsEditing={setIsParkingConfigurationEditing}
            cities={getCitiesByZoneType(ZoneType.PARKING)}
            initialConfiguration={parkingConfiguration}
            zones={getZonesOfType(ZoneType.PARKING)}
            days={companyPolicyData?.days ?? []}
            defaultSelected
          />
        )}
        {availableServices.includes(ServiceType.HIGHWAYS) && (
          <HighwaysConfiguration
            highwaySections={companyPolicyData?.highwaySections ?? []}
            onConfigurationChange={setHighwaysConfiguration}
            setIsEditing={setIsHighwaysConfigurationEditing}
            initialConfiguration={highwaysConfiguration}
            days={companyPolicyData?.days ?? []}
            defaultSelected
          />
        )}
        {availableServices.includes(ServiceType.PUBLIC_TRANSPORT) && (
          <PublicTransportConfiguration
            cities={getCitiesByZoneType(ZoneType.PUBLIC_TRANSPORT)}
            onConfigurationChange={setPublicTransportConfiguration}
            setIsEditing={setIsPublicTransportConfigurationEditing}
            initialConfiguration={publicTransportConfiguration}
            zones={getZonesOfType(ZoneType.PUBLIC_TRANSPORT)}
            days={companyPolicyData?.days ?? []}
            defaultSelected
          />
        )}
      </div>

      <Alert variant="default">
        <AlertDescription className="flex gap-2 items-start">
          <Info className="size-5 text-surface" />
          {t('ONBOARDING.COMPANY_POLICY.ALERT_TIP')}
        </AlertDescription>
      </Alert>

      <Separator />

      <Button
        onClick={() => saveCompanyPolicyConfiguration({ saveAnyway: false })}
        isLoading={isMutationPending}
        disabled={isMutationPending}
        variant="primary"
        size="large"
        wide
      >
        {t('ONBOARDING.COMPANY_POLICY.SAVE_AND_CONTINUE')}
      </Button>

      <UnsavedChangesDialog
        onSave={() => saveCompanyPolicyConfiguration({ saveAnyway: true })}
        setIsOpen={setIsUnsavedChangesDialogOpen}
        isOpen={isUnsavedChangesDialogOpen}
        isPending={isMutationPending}
      />
    </div>
  );
};
